
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type BloomShaderUniforms = {
	uInputTexture: WebGLUniformLocation,
	uTexelSize: WebGLUniformLocation,
	uThreshold: WebGLUniformLocation,
	uBloomIntensity: WebGLUniformLocation,
	uBloomStrength: WebGLUniformLocation
};

export type BloomShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type BloomShaderSettings = ('DOWNSAMPLE' | 'UPSAMPLE' | 'COMPOSITE')[];

export class BloomShader extends ShaderBase<BloomShaderUniforms, BloomShaderAttributes> {

    private static fragmentShaderSource = `
// Uses fullscreen_quad.vert.glsl (vTexCoord varying)
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uInputTexture;
uniform vec2 uTexelSize;

#ifdef NOODLE_DOWNSAMPLE

// Threshold (only used on the first downsample pass)
uniform float uThreshold;

// 13-tap box downsample (Jimenez, "Next Generation Post Processing in Call of Duty")
// Reduces firefly artifacts compared to naive bilinear downsampling.
vec3 downsample13tap(vec2 uv) {
	vec2 ts = uTexelSize;

	// Center cross (weight 0.5 total)
	vec3 a = texture(uInputTexture, uv).rgb;
	vec3 b = texture(uInputTexture, uv + vec2(-ts.x, -ts.y)).rgb;
	vec3 c = texture(uInputTexture, uv + vec2( ts.x, -ts.y)).rgb;
	vec3 d = texture(uInputTexture, uv + vec2(-ts.x,  ts.y)).rgb;
	vec3 e = texture(uInputTexture, uv + vec2( ts.x,  ts.y)).rgb;

	// Outer samples (weight 0.5 total)
	vec3 f = texture(uInputTexture, uv + vec2(-2.0 * ts.x, -2.0 * ts.y)).rgb;
	vec3 g = texture(uInputTexture, uv + vec2( 0.0,        -2.0 * ts.y)).rgb;
	vec3 h = texture(uInputTexture, uv + vec2( 2.0 * ts.x, -2.0 * ts.y)).rgb;
	vec3 i = texture(uInputTexture, uv + vec2(-2.0 * ts.x,  0.0)).rgb;
	vec3 j = texture(uInputTexture, uv + vec2( 2.0 * ts.x,  0.0)).rgb;
	vec3 k = texture(uInputTexture, uv + vec2(-2.0 * ts.x,  2.0 * ts.y)).rgb;
	vec3 l = texture(uInputTexture, uv + vec2( 0.0,         2.0 * ts.y)).rgb;
	vec3 m = texture(uInputTexture, uv + vec2( 2.0 * ts.x,  2.0 * ts.y)).rgb;

	vec3 result = a * 0.125;
	result += (b + c + d + e) * 0.125;
	result += (f + h + k + m) * 0.03125;
	result += (g + i + j + l) * 0.0625;

	return result;
}

void main() {
	vec3 color = downsample13tap(vTexCoord);

	// Soft threshold: smoothly attenuate below the threshold
	if (uThreshold > 0.0) {
		float brightness = max(color.r, max(color.g, color.b));
		float soft = brightness - uThreshold + 0.1;
		soft = clamp(soft / 0.2, 0.0, 1.0);
		soft = soft * soft;
		float contribution = max(soft * brightness, brightness - uThreshold) / max(brightness, 0.0001);
		color *= contribution;
	}

	fragColor = vec4(color, 1.0);
}

#endif

#ifdef NOODLE_UPSAMPLE

uniform float uBloomIntensity;

// 9-tap tent filter for smooth upsampling
vec3 upsample9tap(vec2 uv) {
	vec2 ts = uTexelSize;

	vec3 a = texture(uInputTexture, uv + vec2(-ts.x, -ts.y)).rgb;
	vec3 b = texture(uInputTexture, uv + vec2( 0.0,  -ts.y)).rgb;
	vec3 c = texture(uInputTexture, uv + vec2( ts.x, -ts.y)).rgb;

	vec3 d = texture(uInputTexture, uv + vec2(-ts.x, 0.0)).rgb;
	vec3 e = texture(uInputTexture, uv).rgb;
	vec3 f = texture(uInputTexture, uv + vec2( ts.x, 0.0)).rgb;

	vec3 g = texture(uInputTexture, uv + vec2(-ts.x, ts.y)).rgb;
	vec3 h = texture(uInputTexture, uv + vec2( 0.0,  ts.y)).rgb;
	vec3 i = texture(uInputTexture, uv + vec2( ts.x, ts.y)).rgb;

	vec3 result = e * 4.0;
	result += (b + d + f + h) * 2.0;
	result += (a + c + g + i);
	return result / 16.0;
}

void main() {
	fragColor = vec4(upsample9tap(vTexCoord) * uBloomIntensity, 1.0);
}

#endif

#ifdef NOODLE_COMPOSITE

// Bloom composite is additive-only now: it samples the upsampled glow texture
// (uInputTexture) and writes glow * strength. The caller blits this with
// \`glBlendFunc(ONE, ONE)\` onto the HDR scene FBO. Tonemapping happens later
// in FinalPresentPass so every graphics preset gets the same operator.
uniform float uBloomStrength;

void main() {
	vec3 glow = texture(uInputTexture, vTexCoord).rgb;
	fragColor = vec4(glow * uBloomStrength, 1.0);
}

#endif
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: BloomShaderUniformsContainer;
    public attributesContainer: BloomShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: BloomShaderSettings) {
        super(ctx, BloomShader.fragmentShaderSource, BloomShader.vertexShaderSource, settings);
        this.uniformsContainer = new BloomShaderUniformsContainer(ctx, this);
        this.attributesContainer = new BloomShaderShaderAttributesContainer(ctx, this);
    }
}

class BloomShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: BloomShader) {
        super(ctx);
    }

    public setInputTexture(texture: Texture) : BloomShaderUniformsContainer {
        this.setTextureValue('uInputTexture', this.shader.uniforms.uInputTexture, texture, 'sampler2D');
        return this;
    }

    public setTexelSize(value: vec2 | number[]) : BloomShaderUniformsContainer {
        this.setValue('uTexelSize', this.shader.uniforms.uTexelSize, value, 'vec2');
        return this;
    }

    public setThreshold(value: number) : BloomShaderUniformsContainer {
        this.setValue('uThreshold', this.shader.uniforms.uThreshold, value, 'float');
        return this;
    }

    public setBloomIntensity(value: number) : BloomShaderUniformsContainer {
        this.setValue('uBloomIntensity', this.shader.uniforms.uBloomIntensity, value, 'float');
        return this;
    }

    public setBloomStrength(value: number) : BloomShaderUniformsContainer {
        this.setValue('uBloomStrength', this.shader.uniforms.uBloomStrength, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uInputTexture', uniform: this.shader.uniforms.uInputTexture, dataType: 'sampler2D' }
        ];
    }
}

class BloomShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: BloomShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : BloomShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : BloomShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}